import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { Fragment, ReactNode } from 'react';

/**
 * Lightweight renderer for Carl's assistant text. No markdown dependency —
 * it handles the small, predictable subset Carl emits: **bold**, RO-number
 * badges, order-status badges, and numbered / bulleted lists. Everything
 * else renders as plain paragraphs, preserving line breaks.
 */

/**
 * Chakra colorSchemes matching the platform's OrderTag component (see
 * src/components/OrderTag.tsx) so tags in chat look identical to the ones on
 * the Order Detail page: quotes-only = teal, potential total = yellow,
 * completed = green, archived = gray, save for later = orange, etc.
 * Workflow phases not covered by OrderTag use the platform's phase colors.
 */
const STATUS_STYLES: Record<string, string> = {
    // OrderTag parity
    'quotes only': 'teal',
    'potential total': 'yellow',
    'oem certification': 'purple',
    'oem endorsement': 'blue',
    'dealer referral': 'cyan',
    'save for later': 'orange',
    archived: 'gray',
    completed: 'green',
    // Workflow phases / statuses
    confirmed: 'green',
    ordered: 'blue',
    'ready to order': 'teal',
    quoting: 'purple',
    'in quoting': 'purple',
    rejected: 'red',
    cancelled: 'red',
    're-keying': 'orange',
    rekeying: 'orange',
    pending: 'orange',
};

const STATUS_ALTERNATION = Object.keys(STATUS_STYLES)
    .sort((a, b) => b.length - a.length)
    .join('|');

// Group 1: RO number (e.g. "RO 316987" / "RO#316987"); Group 2: known status word/phrase.
const TOKEN_REGEX = new RegExp(`(\\bRO\\s*#?\\s*\\d+\\b)|\\b(${STATUS_ALTERNATION})\\b`, 'gi');

const RoBadge = ({ text }: { text: string }) => (
    <Badge
        bg="blue.50"
        color="#0071E3"
        fontSize="0.72em"
        fontWeight="bold"
        borderRadius="md"
        px={1.5}
        py="1px"
        verticalAlign="baseline"
        textTransform="none"
    >
        {text.replace(/\s+/g, ' ').toUpperCase()}
    </Badge>
);

/** Rendered with the same recipe as OrderTag (subtle colorScheme badge) for platform parity. */
const StatusBadge = ({ text }: { text: string }) => {
    const colorScheme = STATUS_STYLES[text.toLowerCase()] ?? 'gray';
    return (
        <Badge
            colorScheme={colorScheme}
            variant="subtle"
            px={2}
            py={0.5}
            fontSize="0.72em"
            fontWeight="medium"
            borderRadius="md"
            verticalAlign="baseline"
        >
            {text}
        </Badge>
    );
};

/** Emit RO/status badges inside an already bold/plain text run. */
const renderTokens = (text: string, bold: boolean, keyPrefix: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    TOKEN_REGEX.lastIndex = 0;
    let index = 0;

    const pushText = (value: string) => {
        if (!value) return;
        nodes.push(
            <Text as="span" key={`${keyPrefix}-t${index++}`} fontWeight={bold ? 'semibold' : undefined} color={bold ? 'gray.900' : undefined}>
                {value}
            </Text>,
        );
    };

    while ((match = TOKEN_REGEX.exec(text)) !== null) {
        pushText(text.slice(lastIndex, match.index));
        if (match[1]) {
            nodes.push(<RoBadge key={`${keyPrefix}-ro${index++}`} text={match[1]} />);
        } else if (match[2]) {
            nodes.push(<StatusBadge key={`${keyPrefix}-s${index++}`} text={match[2]} />);
        }
        lastIndex = match.index + match[0].length;
    }
    pushText(text.slice(lastIndex));
    return nodes;
};

/** Render one line: split on **bold**, then tokenize each run for badges. */
const renderInline = (line: string, keyPrefix: string): ReactNode[] => {
    const segments = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return segments.flatMap((segment, segmentIndex) => {
        const boldMatch = segment.match(/^\*\*([^*]+)\*\*$/);
        if (boldMatch) {
            return renderTokens(boldMatch[1], true, `${keyPrefix}-b${segmentIndex}`);
        }
        return renderTokens(segment, false, `${keyPrefix}-n${segmentIndex}`);
    });
};

interface Block {
    type: 'paragraph' | 'ordered' | 'bullet' | 'heading' | 'table';
    lines: string[];
}

const isTableLine = (line: string) => /^\s*\|.*\|\s*$/.test(line);
const isTableSeparator = (line: string) => /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');

/** Split a `| a | b |` row into trimmed cells. */
const splitTableRow = (line: string): string[] =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());

const parseBlocks = (text: string): Block[] => {
    const rawLines = text.replace(/\r\n/g, '\n').split('\n');
    const blocks: Block[] = [];

    for (const raw of rawLines) {
        const line = raw.trimEnd();
        const orderedMatch = line.match(/^\s*\d+\.\s+(.*)$/);
        const bulletMatch = line.match(/^\s*[-*•]\s+(.*)$/);
        const headingMatch = line.match(/^\s*#{1,3}\s+(.*)$/);

        if (isTableLine(line)) {
            const last = blocks[blocks.length - 1];
            if (last?.type === 'table') last.lines.push(line);
            else blocks.push({ type: 'table', lines: [line] });
        } else if (headingMatch) {
            blocks.push({ type: 'heading', lines: [headingMatch[1]] });
        } else if (orderedMatch) {
            const last = blocks[blocks.length - 1];
            if (last?.type === 'ordered') last.lines.push(orderedMatch[1]);
            else blocks.push({ type: 'ordered', lines: [orderedMatch[1]] });
        } else if (bulletMatch) {
            const last = blocks[blocks.length - 1];
            if (last?.type === 'bullet') last.lines.push(bulletMatch[1]);
            else blocks.push({ type: 'bullet', lines: [bulletMatch[1]] });
        } else if (line.trim() === '') {
            // blank line: force a new paragraph block
            if (blocks[blocks.length - 1]?.type === 'paragraph') blocks.push({ type: 'paragraph', lines: [] });
        } else {
            const last = blocks[blocks.length - 1];
            if (last?.type === 'paragraph' && last.lines.length) last.lines.push(line);
            else blocks.push({ type: 'paragraph', lines: [line] });
        }
    }
    return blocks.filter((block) => block.lines.length > 0 || block.type !== 'paragraph');
};

const CarlMessageContent = ({ content }: { content: string }) => {
    const blocks = parseBlocks(content);

    return (
        <Box>
            {blocks.map((block, blockIndex) => {
                if (block.type === 'table') {
                    const rows = block.lines.filter((line) => !isTableSeparator(line)).map(splitTableRow);
                    if (!rows.length) return null;
                    const [header, ...body] = rows;
                    return (
                        <Box
                            key={blockIndex}
                            mt={blockIndex ? 2 : 0}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="lg"
                            overflowX="auto"
                        >
                            <Box as="table" w="100%" sx={{ borderCollapse: 'collapse' }}>
                                <Box as="thead" bg="gray.100">
                                    <Box as="tr">
                                        {header.map((cell, cellIndex) => (
                                            <Box
                                                as="th"
                                                key={cellIndex}
                                                px={3}
                                                py={1.5}
                                                fontSize="xs"
                                                fontWeight="semibold"
                                                color="gray.600"
                                                textAlign="left"
                                                borderBottom="1px solid"
                                                borderColor="gray.200"
                                                whiteSpace="nowrap"
                                            >
                                                {renderInline(cell, `t${blockIndex}-h${cellIndex}`)}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                                <Box as="tbody">
                                    {body.map((row, rowIndex) => (
                                        <Box as="tr" key={rowIndex} _hover={{ bg: 'gray.50' }}>
                                            {row.map((cell, cellIndex) => (
                                                <Box
                                                    as="td"
                                                    key={cellIndex}
                                                    px={3}
                                                    py={1.5}
                                                    fontSize="sm"
                                                    color="gray.800"
                                                    borderBottom={rowIndex === body.length - 1 ? 'none' : '1px solid'}
                                                    borderColor="gray.200"
                                                >
                                                    {renderInline(cell, `t${blockIndex}-r${rowIndex}-${cellIndex}`)}
                                                </Box>
                                            ))}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    );
                }
                if (block.type === 'heading') {
                    return (
                        <Text key={blockIndex} fontSize="sm" fontWeight="bold" color="gray.900" mt={blockIndex ? 3 : 0} mb={1}>
                            {renderInline(block.lines[0], `h${blockIndex}`)}
                        </Text>
                    );
                }
                if (block.type === 'ordered' || block.type === 'bullet') {
                    const ordered = block.type === 'ordered';
                    return (
                        <Box key={blockIndex} mt={blockIndex ? 2 : 0}>
                            {block.lines.map((line, lineIndex) => (
                                <Flex key={lineIndex} gap={2.5} mb={1.5} align="flex-start">
                                    {ordered ? (
                                        <Flex
                                            flexShrink={0}
                                            align="center"
                                            justify="center"
                                            minW="20px"
                                            h="20px"
                                            mt="1px"
                                            borderRadius="md"
                                            bg="blue.50"
                                            color="#0071E3"
                                            fontSize="xs"
                                            fontWeight="bold"
                                        >
                                            {lineIndex + 1}
                                        </Flex>
                                    ) : (
                                        <Box flexShrink={0} w="5px" h="5px" borderRadius="full" bg="#0071E3" mt="8px" />
                                    )}
                                    <Text fontSize="sm" color="gray.800" lineHeight="tall">
                                        {renderInline(line, `l${blockIndex}-${lineIndex}`)}
                                    </Text>
                                </Flex>
                            ))}
                        </Box>
                    );
                }
                return (
                    <Text key={blockIndex} fontSize="sm" color="gray.800" lineHeight="tall" mt={blockIndex ? 2 : 0} whiteSpace="pre-wrap">
                        {block.lines.map((line, lineIndex) => (
                            <Fragment key={lineIndex}>
                                {lineIndex > 0 && <br />}
                                {renderInline(line, `p${blockIndex}-${lineIndex}`)}
                            </Fragment>
                        ))}
                    </Text>
                );
            })}
        </Box>
    );
};

export default CarlMessageContent;
